import classNames from 'clsx';
import { useMemo } from 'react';
import type { MdComponentIndexProps } from '../type';
import { useInnerIconPropsNode } from './parser';
import { UrlIcon } from './UrlIcon';

export type SupportIconProps = {
  icon?: string; // only icon name
  iconSize?: number | string | null;
  iconColor?: string;
};

const presetColor = [
  'pink',
  'blue',
  'purple',
  'cyan',
  'green',
  'magenta',
  'red',
  'orange',
  'yellow',
  'volcano',
  'geekblue',
  'lime',
  'gold',
  'primary',
];
function InnerSupportIconPropsNode({
  icon,
  iconSize,
  iconColor,
  className,
  style,
  ...props
}: {
  icon: string; // icon name or svg
  iconSize?: number | string | null;
  iconColor?: string;
} & MdComponentIndexProps & { className?: string; style?: React.CSSProperties }) {
  const IconSize = useMemo(() => {
    if (typeof iconSize === 'number') return iconSize;
    if (typeof iconSize === 'string') {
      try {
        return parseInt(iconSize, 10);
      } catch (e) {
        return iconSize;
      }
    }
    return undefined;
  }, [iconSize]);

  const color = useMemo(() => {
    if (typeof iconColor === 'string') {
      const val = iconColor.trim();
      if (presetColor.includes(val)) {
        return `var(--ui-${val}-base)`;
      }
      return val;
    }
    return undefined;
  }, [iconColor]);

  const iconTemplate = useInnerIconPropsNode(icon);

  if (!iconTemplate) return null;
  return (
    <span
      className={classNames('markdown-component-icon', className)}
      style={{
        fontSize: IconSize,
        color: color,
        ...style,
      }}
      data-first={props['data-first']}
      data-last={props['data-last']}
    >
      {iconTemplate}
    </span>
  );
}

function SupportIconPropsNode({
  icon,
  iconSize,
  iconColor,
  ...props
}: SupportIconProps & MdComponentIndexProps & { className?: string; style?: React.CSSProperties }) {
  if (!icon) return null;
  if (typeof icon !== 'string') return null;
  if (icon.startsWith('http://') || icon.startsWith('https://')) {
    return <UrlIcon icon={icon} iconSize={iconSize} iconColor={iconColor} {...props} />;
  }
  return (
    <InnerSupportIconPropsNode icon={icon} iconSize={iconSize} iconColor={iconColor} {...props} />
  );
}

export default SupportIconPropsNode;
